import { QueryClient } from '@tanstack/react-query';
import type {
  TreeSpecies,
  CommunityTree,
  SiteAssessment,
  GeocodeResult,
  CommunityTreeSuggestion,
  BoundingBox,
  ApiError,
} from '../types';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';

// Create a custom fetch wrapper with error handling
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error: ApiError = {
        message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        code: errorData.code || response.status.toString(),
        details: errorData.details,
      };
      throw error;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof Error && 'message' in error) {
      throw error;
    }
    throw new Error('Network error occurred');
  }
}

// API Functions
export const api = {
  // Tree Species
  getTreeSpecies: async (): Promise<TreeSpecies[]> => {
    const response = await apiRequest<{success: boolean, message: string, data: TreeSpecies[], pagination?: any}>('/tree-species');
    return response.data;
  },

  getTreeSpeciesById: async (id: number): Promise<TreeSpecies> => {
    const response = await apiRequest<{success: boolean, message: string, data: TreeSpecies}>(`/tree-species/${id}`);
    return response.data;
  },

  // Community Trees
  getCommunityTrees: async (bbox?: BoundingBox): Promise<CommunityTree[]> => {
    const params = bbox ? `?bbox=${bbox.join(',')}` : '';
    const response = await apiRequest<{success: boolean, message: string, data: CommunityTree[], pagination?: any}>(`/community-trees${params}`);
    return response.data;
  },

  getCommunityTreeById: async (id: number): Promise<CommunityTree> => {
    const response = await apiRequest<{success: boolean, message: string, data: CommunityTree}>(`/community-trees/${id}`);
    return response.data;
  },

  createCommunityTree: async (treeData: {
    species_id: number;
    longitude: number;
    latitude: number;
    address?: string;
    neighborhood?: string;
    planting_date?: string;
    site_assessment_score?: number;
    site_factors?: any;
    ownership_type?: 'community' | 'private' | 'adopted';
    is_public?: boolean;
    created_by?: string;
  }): Promise<CommunityTree> => {
    const response = await apiRequest<{success: boolean, message: string, data: CommunityTree}>('/community-trees', {
      method: 'POST',
      body: JSON.stringify(treeData),
    });
    return response.data;
  },

  // Site Assessment
  assessSite: async (latitude: number, longitude: number): Promise<SiteAssessment> => {
    return apiRequest<SiteAssessment>('/assess-site', {
      method: 'POST',
      body: JSON.stringify({ latitude, longitude }),
    });
  },

  // Geocoding
  geocodeAddress: async (address: string): Promise<GeocodeResult[]> => {
    const response = await apiRequest<{success: boolean, data: GeocodeResult[]}>('/geocoding/search', {
      method: 'POST',
      body: JSON.stringify({ address }),
    });
    return response.data;
  },

  // Community Suggestions
  submitTreeSuggestion: async (suggestion: CommunityTreeSuggestion): Promise<{ id: number; message: string }> => {
    return apiRequest<{ id: number; message: string }>('/community/suggestions', {
      method: 'POST',
      body: JSON.stringify(suggestion),
    });
  },

  getTreeSuggestions: async (): Promise<CommunityTreeSuggestion[]> => {
    return apiRequest<CommunityTreeSuggestion[]>('/community/suggestions');
  },
};

// React Query Configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'code' in error) {
          const code = parseInt(error.code as string);
          if (code >= 400 && code < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Query Keys
export const queryKeys = {
  treeSpecies: ['tree-species'] as const,
  treeSpeciesById: (id: number) => ['tree-species', id] as const,
  communityTrees: (bbox?: BoundingBox) => ['community-trees', bbox] as const,
  communityTreeById: (id: number) => ['community-tree', id] as const,
  createCommunityTree: ['create-community-tree'] as const,
  siteAssessment: (lat: number, lng: number) => ['site-assessment', lat, lng] as const,
  geocode: (address: string) => ['geocode', address] as const,
  treeSuggestions: ['tree-suggestions'] as const,
};

// Error handling utility
export const handleApiError = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    return error.message as string;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
